
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { usePlayerStats } from '@/hooks/usePlayerStats';
import { useState } from 'react';
import StatSubmissionModal from './StatSubmissionModal';

interface PlayerStatsDisplayProps {
  playerId: string;
  showSubmitButton?: boolean;
}

const PlayerStatsDisplay = ({ playerId, showSubmitButton = true }: PlayerStatsDisplayProps) => {
  const { stats, totalStats, loading } = usePlayerStats(playerId);
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-600 rounded w-1/4 mb-2"></div>
          <div className="h-8 bg-gray-600 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Total Stats Summary */}
      {totalStats.length > 0 && (
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center justify-between">
              Career Statistics
              {showSubmitButton && (
                <Button
                  onClick={() => setShowSubmissionModal(true)}
                  size="sm"
                  className="bg-sports-green text-black hover:bg-green-600"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Submit Stats
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {totalStats.map((totalStat) => (
                <div key={`${totalStat.player_id}-${totalStat.sport_id}`} className="space-y-2">
                  <div className="text-sm text-gray-400">
                    Sport: {totalStat.sport_id}
                  </div>
                  <div className="grid grid-cols-1 gap-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Games:</span>
                      <span className="text-white">{totalStat.total_games_played}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Points:</span>
                      <span className="text-white">{totalStat.total_points}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Assists:</span>
                      <span className="text-white">{totalStat.total_assists}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Goals:</span>
                      <span className="text-white">{totalStat.total_goals}</span>
                    </div>
                    {totalStat.total_rebounds > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-300">Rebounds:</span>
                        <span className="text-white">{totalStat.total_rebounds}</span>
                      </div>
                    )}
                    {totalStat.total_saves > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-300">Saves:</span>
                        <span className="text-white">{totalStat.total_saves}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Individual Game Stats */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center justify-between">
            Game-by-Game Stats
            {showSubmitButton && totalStats.length === 0 && (
              <Button
                onClick={() => setShowSubmissionModal(true)}
                size="sm"
                className="bg-sports-green text-black hover:bg-green-600"
              >
                <Plus className="h-4 w-4 mr-1" />
                Submit Stats
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {stats.length > 0 ? (
            <div className="space-y-3">
              {stats.map((stat) => (
                <div
                  key={stat.stat_id}
                  className="flex items-center justify-between p-3 bg-gray-700 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div>
                      <p className="font-medium text-white">
                        {stat.stat_type}: {stat.stat_value}
                      </p>
                      <p className="text-sm text-gray-400">
                        Game ID: {stat.game_id} • Sport ID: {stat.sport_id}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(stat.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant={stat.is_confirmed ? "default" : "secondary"}
                      className={
                        stat.is_confirmed
                          ? "bg-sports-green text-black"
                          : "bg-yellow-600 text-white"
                      }
                    >
                      {stat.is_confirmed ? 'Confirmed' : 'Pending'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-400 py-8">
              No stats submitted yet. Submit your first stats to get started!
            </div>
          )}
        </CardContent>
      </Card>

      <StatSubmissionModal
        isOpen={showSubmissionModal}
        onClose={() => setShowSubmissionModal(false)}
        playerId={playerId}
      />
    </div>
  );
};

export default PlayerStatsDisplay;
